import { useState, useCallback } from 'react';
import { supabase, apiUrl } from '../config/supabase';
import { DEFAULT_SETTINGS } from '../config/styles';

// أعمدة صريحة بدون عمود password — عشان المتصفح ميحمّلش الباسوردات نهائيًا
const EMPLOYEE_COLS = 'id, username, name_ar, name_en, role_id, is_active, created_at, can_manage_courses, can_manage_services, can_manage_settings, can_view_dashboard, can_view_customers, can_manage_employees, can_manage_lessons, can_view_enrollments, roles(*)';

// توكن جلسة الموظف (للعمليات الإدارية على السيرفر)
const empToken = () => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } };

export function useData() {
  const [courses,         setCourses]         = useState([]);
  const [services,        setServices]        = useState([]);
  const [settings,        setSettings]        = useState(DEFAULT_SETTINGS);
  const [customers,       setCustomers]       = useState([]);
  const [enrollments,     setEnrollments]     = useState([]);
  const [payments,        setPayments]        = useState([]);
  const [lessons,         setLessons]         = useState([]);
  const [employees,       setEmployees]       = useState([]);
  const [roles,           setRoles]           = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [progress,        setProgress]        = useState([]); // ← جديد
  const [studentWorks,    setStudentWorks]    = useState([]); // أعمال الطلاب
  const [reviews,         setReviews]         = useState([]); // آراء الطلاب
  const [categories,      setCategories]      = useState([]); // التصنيفات
  const [instructors,     setInstructors]     = useState([]); // المدربين
  const [serviceReviews,  setServiceReviews]  = useState([]); // آراء باقات الأجنسي
  const [agencyWorks,     setAgencyWorks]     = useState([]); // أعمالنا (الأجنسي)
  const [loading,         setLoading]         = useState(true);

  // البيانات الحساسة (عملاء/مدفوعات/رسايل) — بتتجاب من السيرفر بتوكن الموظف فقط.
  // المفتاح العام في المتصفح ميقدرش يقراها خالص (محصورة سيرفر سايد + RLS).
  const loadAdminData = useCallback(async () => {
    const token = empToken();
    if (!token) { setCustomers([]); setPayments([]); setContactMessages([]); setEmployees([]); return; }
    try {
      const res = await fetch(apiUrl('/api/admin/data'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.status === 401) {
        try { window.dispatchEvent(new CustomEvent('emp-session-expired')); } catch {}
      }
      if (res.ok && r.ok) {
        setCustomers(r.customers || []);
        setPayments(r.payments || []);
        setContactMessages(r.contactMessages || []);
        setEmployees(r.employees || []);
      }
    } catch {
      // فشل صامت — الداشبورد يفضل شغال بباقي البيانات
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [c, s, st, en, les, rol, sw, rv, cat, inst, srv, awk] = await Promise.all([
      supabase.from('courses').select('*').order('id'),
      supabase.from('services').select('*').order('id'),
      supabase.from('settings').select('*').eq('id', 1).single(),
      supabase.from('enrollments').select('*'),
      supabase.from('lessons').select('id, course_id, title_ar, title_en, order_num, duration, is_free, created_at, video_source, description_ar, description_en, attachments').order('order_num'),
      supabase.from('roles').select('*').order('id'),
      supabase.from('student_works').select('*').order('order_num'),
      supabase.from('course_reviews').select('*').order('order_num'),
      supabase.from('categories').select('*').order('order_num'),
      supabase.from('instructors').select('*').order('order_num'),
      supabase.from('service_reviews').select('*').order('order_num'),
      supabase.from('agency_works').select('*').order('order_num'),
    ]);
    if (c.data)    setCourses(c.data);
    if (s.data)    setServices(s.data);
    if (st.data)   setSettings(st.data);
    if (en.data)   setEnrollments(en.data);
    if (les.data)  setLessons(les.data);
    if (rol.data)  setRoles(rol.data);
    if (sw.data)   setStudentWorks(sw.data);
    if (rv.data)   setReviews(rv.data);
    if (cat.data)  setCategories(cat.data);
    if (inst.data) setInstructors(inst.data);
    if (srv.data)  setServiceReviews(srv.data);
    if (awk.data)  setAgencyWorks(awk.data);
    // البيانات الحساسة عبر السيرفر (لو فيه موظف مسجّل دخول)
    await loadAdminData();
    setLoading(false);
  }, [loadAdminData]);

  // ── Progress ────────────────────────────────
  // بيتحمل بعد login العميل
  const loadProgress = useCallback(async (customerId) => {
    if (!customerId || String(customerId).startsWith('guest_')) return;
    const { data } = await supabase
      .from('lesson_progress')
      .select('lesson_id, course_id, watched_at')
      .eq('customer_id', customerId);
    if (data) setProgress(data);
  }, []);

  // بيتعمل call لما العميل يشوف الحلقة
  const markWatched = useCallback(async (customerId, lessonId, courseId) => {
    if (!customerId || String(customerId).startsWith('guest_')) return;
    let custToken = ''; try { custToken = localStorage.getItem('cust_token') || ''; } catch {}
    if (!custToken) return; // مفيش توكن عميل صالح → بلاش نحاول
    let ok = false;
    try {
      const res = await fetch(apiUrl('/api/progress/mark'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: custToken, lessonId, courseId }),
      });
      const r = await res.json().catch(() => ({}));
      ok = res.ok && r.ok;
    } catch {
      ok = false; // فشل صامت — متبوّظش تجربة المشاهدة
    }
    if (ok) {
      setProgress(p => {
        if (p.some(x => x.lesson_id === lessonId)) return p;
        return [...p, { lesson_id: lessonId, course_id: courseId, watched_at: new Date().toISOString() }];
      });
    }
  }, []);

  const isWatched = useCallback((lessonId) =>
    progress.some(p => p.lesson_id === lessonId),
  [progress]);

  // نسبة الإتمام للكورس (0-100)
  const courseProgress = useCallback((courseId, allLessons) => {
    const cl = allLessons.filter(l => l.course_id === courseId);
    if (!cl.length) return 0;
    const watched = cl.filter(l => progress.some(p => p.lesson_id === l.id)).length;
    return Math.round((watched / cl.length) * 100);
  }, [progress]);

  // ── helpers ──
  const isEnrolled = (customerId, courseId) =>
    enrollments.some(e => e.customer_id === customerId && e.course_id === courseId && e.status === 'active');

  const cLessons = (courseId) =>
    lessons.filter(l => l.course_id === courseId).sort((a, b) => a.order_num - b.order_num);

  // ── Course CRUD ──
  // ── حفظ/حذف المحتوى عبر السيرفر (endpoint عام) ──
  const contentSave = async (table, row) => {
    try {
      const res = await fetch(apiUrl('/api/admin/content-save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), table, row }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.ok && r.ok && r.row) return { ok: true, row: r.row };
      return { error: r.message || 'فشل الحفظ' };
    } catch (e) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };
  const contentDelete = async (table, id) => {
    try {
      const res = await fetch(apiUrl('/api/admin/content-delete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), table, id }),
      });
      const r = await res.json().catch(() => ({}));
      return (res.ok && r.ok) ? { ok: true } : { error: r.message || 'فشل الحذف' };
    } catch (e) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };

  const saveCourse = async (data, editing) => {
    const r = await contentSave('courses', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setCourses(x => editing ? x.map(c => (c.id === r.row.id ? r.row : c)) : [...x, r.row]);
    return r;
  };
  const deleteCourse = async (id) => {
    const r = await contentDelete('courses', id);
    if (r.ok) setCourses(x => x.filter(c => c.id !== id));
    return r;
  };

  // ── Categories CRUD ──
  const saveCategory = async (data, editing) => {
    const r = await contentSave('categories', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setCategories(x => editing ? x.map(c => (c.id === r.row.id ? r.row : c)) : [...x, r.row]);
    return r;
  };
  const deleteCategory = async (id) => {
    const r = await contentDelete('categories', id);
    if (r.ok) setCategories(x => x.filter(c => c.id !== id));
    return r;
  };

  // ── Instructors CRUD ──
  const saveInstructor = async (data, editing) => {
    const r = await contentSave('instructors', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setInstructors(x => editing ? x.map(i => (i.id === r.row.id ? r.row : i)) : [...x, r.row]);
    return r;
  };
  const deleteInstructor = async (id) => {
    const r = await contentDelete('instructors', id);
    if (r.ok) setInstructors(x => x.filter(i => i.id !== id));
    return r;
  };

  // ── Student Works CRUD ──
  const saveStudentWork = async (data, editing) => {
    const r = await contentSave('student_works', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setStudentWorks(x => editing ? x.map(w => (w.id === r.row.id ? r.row : w)) : [...x, r.row]);
    return r;
  };
  const deleteStudentWork = async (id) => {
    const r = await contentDelete('student_works', id);
    if (r.ok) setStudentWorks(x => x.filter(w => w.id !== id));
    return r;
  };

  // ── Service Reviews (آراء باقات الأجنسي) ──
  const saveServiceReview = async (data, editing) => {
    const r = await contentSave('service_reviews', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setServiceReviews(x => editing ? x.map(r => (r.id === r.row.id ? r.row : r)) : [...x, r.row]);
    return r;
  };
  const deleteServiceReview = async (id) => {
    const r = await contentDelete('service_reviews', id);
    if (r.ok) setServiceReviews(x => x.filter(r => r.id !== id));
    return r;
  };
  // رأي من العميل نفسه — يتسجّل "تحت المراجعة" (is_approved=false)
  const addPublicServiceReview = async (payload) => {
    try {
      const { data: d, error } = await supabase.from('service_reviews')
        .insert({ ...payload, is_approved: false }).select();
      if (error) return { error: error.message };
      const row = Array.isArray(d) ? d[0] : d;
      if (row) setServiceReviews(x => [...x, row]);
      return { ok: true };
    } catch (e) {
      return { error: String(e?.message || e) };
    }
  };
  // الموظف يوافق على رأي العميل
  const approveServiceReview = async (id) => {
    const r = await contentSave('service_reviews', { id, is_approved: true });
    if (r.ok && r.row) setServiceReviews(x => x.map(rv => rv.id === r.row.id ? r.row : rv));
  };

  // ── Agency Works (أعمالنا) ──
  const saveAgencyWork = async (data, editing) => {
    const r = await contentSave('agency_works', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setAgencyWorks(x => editing ? x.map(w => (w.id === r.row.id ? r.row : w)) : [...x, r.row]);
    return r;
  };
  const deleteAgencyWork = async (id) => {
    const r = await contentDelete('agency_works', id);
    if (r.ok) setAgencyWorks(x => x.filter(w => w.id !== id));
    return r;
  };

  // ── Reviews (آراء الطلاب) ──
  // الطالب يضيف رأيه — يبدأ غير معتمد (is_approved=false) لحد ما الأدمن يوافق
  const addReview = async (review) => {
    try {
      const { data: d, error } = await supabase.from('course_reviews')
        .insert({ ...review, is_approved: false })
        .select();
      if (error) return { error };
      const row = Array.isArray(d) ? d[0] : d;
      if (row) setReviews(x => [...x, row]);
      return { data: row || true };
    } catch (e) {
      return { error: e };
    }
  };
  const approveReview = async (id) => {
    const r = await contentSave('course_reviews', { id, is_approved: true });
    if (r.ok && r.row) setReviews(x => x.map(rv => rv.id === r.row.id ? r.row : rv));
  };
  const deleteReview = async (id) => {
    const r = await contentDelete('course_reviews', id);
    if (r.ok) setReviews(x => x.filter(rv => rv.id !== id));
    return r;
  };

  // ── Lesson CRUD ──
  const saveLesson = async (lessonData, editing) => {
    try {
      const res = await fetch(apiUrl('/api/admin/lesson-save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), lesson: { ...lessonData, id: editing ? editing.id : null } }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.ok && r.ok && r.lesson) {
        const d = r.lesson;
        setLessons(x => editing ? x.map(l => (l.id === d.id ? d : l)) : [...x, d]);
        return { ok: true };
      }
      return { error: r.message || 'فشل حفظ الحلقة' };
    } catch (err) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };
  const deleteLesson = async (id) => {
    try {
      const res = await fetch(apiUrl('/api/admin/lesson-delete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), lessonId: id }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.ok && r.ok) { setLessons(x => x.filter(l => l.id !== id)); return { ok: true }; }
      return { error: r.message || 'فشل حذف الحلقة' };
    } catch (err) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };

  // ── Service CRUD ──
  const saveService = async (data, editing) => {
    const r = await contentSave('services', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setServices(x => editing ? x.map(s => (s.id === r.row.id ? r.row : s)) : [...x, r.row]);
    return r;
  };
  const deleteService = async (id) => {
    const r = await contentDelete('services', id);
    if (r.ok) setServices(x => x.filter(s => s.id !== id));
    return r;
  };

  // ── Employee CRUD ──
  // الحفظ بقى عبر السيرفر (/api/admin/employee-save) — الباسورد بيتشفّر سيرفر سايد
  // والتحقق من الصلاحية بالتوكن. بنرجّع { ok } أو { error } عشان App يعرض الخطأ الحقيقي.
  const saveEmployee = async (emp, editing) => {
    try {
      const token = (() => { try { return localStorage.getItem('emp_token') || ''; } catch { return ''; } })();
      const res = await fetch(apiUrl('/api/admin/employee-save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, employee: { ...emp, id: editing?.id || emp.id || null } }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل حفظ الموظف' };
      const d = r.employee;
      if (d) setEmployees(x => editing ? x.map(e => e.id === d.id ? d : e) : [...x, d]);
      return { ok: true };
    } catch (e) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };
  const deleteEmployee = async (id) => {
    try {
      const res = await fetch(apiUrl('/api/admin/employee-delete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), employeeId: id }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل حذف الموظف' };
      setEmployees(x => x.filter(e => e.id !== id));
      return { ok: true };
    } catch (e) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };
  const toggleEmployeeStatus = async (emp) => {
    try {
      const token = empToken();
      const res = await fetch(apiUrl('/api/admin/employee-toggle'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, employeeId: emp.id, isActive: !emp.is_active }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.ok && r.ok && r.employee) {
        setEmployees(x => x.map(e => (e.id === r.employee.id ? r.employee : e)));
        return r.employee;
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  // ── إدارة العملاء من لوحة التحكم (عبر السيرفر — الباسورد بيتشفّر) ──
  const createCustomer = async (payload) => {
    try {
      const res = await fetch(apiUrl('/api/admin/customer-create'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), ...payload }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل إنشاء العميل' };
      if (r.customer) setCustomers(x => [r.customer, ...x]);
      return { ok: true, customer: r.customer };
    } catch (e) { return { error: 'فشل الاتصال بالسيرفر' }; }
  };
  const updateCustomer = async (customerId, fields) => {
    try {
      const res = await fetch(apiUrl('/api/admin/customer-update'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), customerId, ...fields }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل تعديل العميل' };
      if (r.customer) setCustomers(x => x.map(c => c.id === r.customer.id ? r.customer : c));
      return { ok: true, customer: r.customer };
    } catch (e) { return { error: 'فشل الاتصال بالسيرفر' }; }
  };
  const setCustomerPassword = async (customerId, newPassword) => {
    try {
      const res = await fetch(apiUrl('/api/admin/customer-set-password'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), customerId, newPassword }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل تحديث الباسورد' };
      return { ok: true };
    } catch (e) { return { error: 'فشل الاتصال بالسيرفر' }; }
  };

  // ── Role CRUD ──
  const saveRole = async (data, editing) => {
    const r = await contentSave('roles', editing ? { ...data, id: editing.id } : data);
    if (r.ok && r.row) setRoles(x => editing ? x.map(r => (r.id === r.row.id ? r.row : r)) : [...x, r.row]);
    return r;
  };
  const deleteRole = async (id) => {
    const r = await contentDelete('roles', id);
    if (r.ok) setRoles(x => x.filter(r => r.id !== id));
    return r;
  };

  // ── Settings ──
  const saveSettings = async (patch) => {
    try {
      const res = await fetch(apiUrl('/api/admin/settings-save'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), settings: patch }),
      });
      const r = await res.json().catch(() => ({}));
      if (res.ok && r.ok && r.settings) { setSettings(r.settings); return { ok: true }; }
      return { error: r.message || 'فشل حفظ الإعدادات' };
    } catch (err) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };

  // ── Payments ──
  // التفعيل بقى عبر السيرفر (/api/admin/approve-payment) — محمي بالتوكن،
  // وبيكتب الـ enrollment بالـ service key (مش بالمفتاح العام).
  const approvePayment = async (payment) => {
    try {
      const res = await fetch(apiUrl('/api/admin/approve-payment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), paymentId: payment.id }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل التفعيل' };
      await loadAll();
      return { ok: true };
    } catch (err) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };
  const rejectPayment = async (id) => {
    try {
      const res = await fetch(apiUrl('/api/admin/reject-payment'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: empToken(), paymentId: id }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل رفض الدفع' };
      await loadAll();
      return { ok: true };
    } catch (err) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };
  // الدفع اليدوي بقى عبر السيرفر — السعر بيتحسب من الـ DB (مش من العميل)
  const addPayment = async ({ courseId, customerId, method, note, couponCode }) => {
    try {
      const res = await fetch(apiUrl('/api/payment/manual'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseId, customerId, method, note, couponCode: couponCode || null }),
      });
      const r = await res.json().catch(() => ({}));
      if (!res.ok || !r.ok) return { error: r.message || 'فشل تسجيل الدفع' };
      return { ok: true };
    } catch (err) {
      return { error: 'فشل الاتصال بالسيرفر' };
    }
  };

  // ── Contact ──
  const addContactMessage = async (data) => await supabase.from('contact_messages').insert(data);
  const refreshMessages = async () => { await loadAdminData(); };

  // ── Thumbnail Upload ──────────────────────
  const uploadThumbnail = async (file) => {
    const ext      = file.name.split('.').pop();
    const fileName = `course_${Date.now()}.${ext}`;
    const { data, error } = await supabase.storage
      .from('thumbnails')
      .upload(fileName, file, { upsert: true, contentType: file.type });
    if (error) { console.error('Upload error:', error.message); return null; }
    const { data: pub } = supabase.storage.from('thumbnails').getPublicUrl(fileName);
    return pub?.publicUrl || null;
  };

  // رفع صورة عامة (المدرب / البانر) — نفس bucket الـ thumbnails
  const uploadImage = async (file, prefix = 'img') => {
    const ext      = file.name.split('.').pop();
    const fileName = `${prefix}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage
      .from('thumbnails')
      .upload(fileName, file, { upsert: true, contentType: file.type });
    if (error) { console.error('Upload error:', error.message); return null; }
    const { data: pub } = supabase.storage.from('thumbnails').getPublicUrl(fileName);
    return pub?.publicUrl || null;
  };

  // رفع ملف مرفق (PDF / Word / أي ملف) — bucket الـ attachments
  // بيرجّع { url, name } عشان نحتفظ باسم الملف الأصلي
  const uploadFile = async (file) => {
    const safe = file.name.replace(/[^\w.\-]+/g, '_');           // اسم آمن
    const fileName = `att_${Date.now()}_${safe}`;
    const { error } = await supabase.storage
      .from('attachments')
      .upload(fileName, file, { upsert: true, contentType: file.type || 'application/octet-stream' });
    if (error) { console.error('Upload file error:', error.message); return null; }
    const { data: pub } = supabase.storage.from('attachments').getPublicUrl(fileName);
    return pub?.publicUrl ? { url: pub.publicUrl, name: file.name } : null;
  };

  return {
    courses, services, settings, customers, enrollments,
    payments, lessons, employees, roles, contactMessages,
    progress, studentWorks, reviews, categories, instructors, loading,
    serviceReviews, agencyWorks,
    setCustomers,
    loadAll, loadAdminData, loadProgress,
    markWatched, isWatched, courseProgress,
    isEnrolled, cLessons,
    saveCourse, deleteCourse,
    saveCategory, deleteCategory,
    saveInstructor, deleteInstructor,
    saveStudentWork, deleteStudentWork,
    saveServiceReview, deleteServiceReview, addPublicServiceReview, approveServiceReview,
    saveAgencyWork, deleteAgencyWork,
    addReview, approveReview, deleteReview,
    saveLesson, deleteLesson,
    saveService, deleteService,
    saveEmployee, deleteEmployee, toggleEmployeeStatus,
    createCustomer, updateCustomer, setCustomerPassword,
    saveRole, deleteRole,
    saveSettings,
    approvePayment, rejectPayment, addPayment,
    addContactMessage, refreshMessages,
    uploadThumbnail, uploadImage, uploadFile,
  };
}
